import { cn } from '@heroui/react';
import { Check, NavArrowDown, Search } from 'iconoir-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import i18n from '~/common/helpers/i18n';

interface Realm {
  id: string;
  name: string;
  companyName: string;
}

interface RealmSelectProps {
  activeRealm: string;
  allIds?: string[];
  byId?: Record<string, Realm>;
  canSelect?: boolean;
  currentPath: string;
}

const TRIGGER_CLASS =
  'flex h-10 items-center rounded-md border border-solid border-outline-hover px-2 py-1 text-sm font-semibold leading-5 tracking-[0.1px] text-on-surface';

export const RealmSelect: FC<RealmSelectProps> = ({
  activeRealm = '',
  allIds = [],
  byId = {},
  canSelect = false,
  currentPath
}) => {
  const realms = allIds.map(id => byId[id]).filter(Boolean);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sorted = useMemo(
    () => [...realms].sort((a, b) => a.name.localeCompare(b.name)),
    [realms]
  );
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return sorted;
    return sorted.filter(
      realm =>
        realm.name.toLowerCase().includes(term) ||
        realm.companyName?.toLowerCase().includes(term)
    );
  }, [sorted, query]);

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const onPointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!canSelect || realms.length <= 1) {
    return (
      <div data-testid='realm-selection-menu' className={TRIGGER_CLASS}>
        <span data-testid='selected-text' className='truncate'>
          {activeRealm}
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className='relative'>
      <button
        type='button'
        data-testid='realm-selection-menu'
        aria-label='Switch realm'
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        className={cn(
          TRIGGER_CLASS,
          'max-w-[220px] cursor-pointer gap-2 outline-none transition-colors hover:bg-surface-container-low focus-visible:ring-2 focus-visible:ring-secondary/30'
        )}
      >
        <span data-testid='selected-text' className='truncate'>
          {activeRealm}
        </span>
        <NavArrowDown
          className={cn(
            'h-4 w-4 shrink-0 text-on-surface transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open ? (
        <div className='absolute left-0 z-50 mt-1 w-60 overflow-hidden rounded-lg border border-solid border-outline bg-surface shadow-lg'>
          <div className='flex items-center gap-2 border-b border-solid border-outline px-2.5 py-2'>
            <Search className='h-3.5 w-3.5 shrink-0 text-on-surface-variant' />
            <input
              ref={inputRef}
              type='text'
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={i18n.t('label.search_realms')!}
              aria-label={i18n.t('label.search_realms')!}
              className='w-full bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant'
            />
          </div>
          <ul className='max-h-60 overflow-y-auto p-1'>
            {filtered.length === 0 ? (
              <li
                data-testid='realm-no-results'
                className='px-2 py-3 text-center text-sm text-on-surface-variant'
              >
                {i18n.t('label.no_realms_found')}
              </li>
            ) : (
              filtered.map(realm => {
                const isActive = realm.name === activeRealm;
                return (
                  <li key={realm.id}>
                    <a
                      data-testid='link'
                      href={currentPath.replace(activeRealm, realm.name)}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={() => setOpen(false)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-surface-container-low',
                        isActive
                          ? 'font-semibold text-on-surface'
                          : 'text-on-surface'
                      )}
                    >
                      <span data-testid='value-text' className='truncate'>
                        {realm.name}
                      </span>
                      {isActive ? (
                        <Check className='h-3.5 w-3.5 shrink-0' />
                      ) : null}
                    </a>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
